import { MappingPlasticsMaterialToQualityAlert } from './../../../../../../../../libs/prism-shared/src/lib/models/mapping-plastics-material-to-quality-alert.model';
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { BatchDeleteEntityGroups, BatchSetEntityGroups, BillOfMaterialsLineItemType, Collection, MergeArrayValues, PlasticsFinishingStation, PlasticsFinishingTask, InventoryTransaction, PlasticsHardwareItem, PlasticsOrderedProductStatus, PlasticsQualityAlert, ProductVersion, ProductVersionStatus, Resource, SaveStatus, toEntityDict, VendorHardwareData } from "@prism/prism-shared";
import { catchError, combineLatest, exhaustMap, forkJoin, map, mergeMap, Observable, of, pipe, switchMap, tap, withLatestFrom } from "rxjs";
import { PlasticsActions, PlasticsSelectors } from ".";
import { PadNumberPipe } from "../../../pipes/pad-number/pad-number-pipe";
import { PlasticsApiService } from "../../../services/plastics-api.service";
import { PrismCommonApiService } from "../../../services/prism-common-api.service";
import { CoreSelectors } from "../../core";
import { PrismCommonActions } from "../common";


@Injectable({ providedIn: 'root' })
export class PlasticsEffects {

    constructor(
        public store: Store,
        private actions$: Actions,
        private plasticsApiService: PlasticsApiService,
        private prismCommonApiService: PrismCommonApiService,
        private padPartNumberPipe: PadNumberPipe,
    ) { }

    private appendTimestamp<T>(
        accessor: (obj: Record<string, T>) => T,
    ) {
        return pipe(
            map(accessor),
            withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
            map(([entity, currentUser]) => ({
                ...entity,
                lastSavedOn: new Date(),
                lastSavedById: currentUser.id
            }))
        )
    }


    LoadHardwareItems$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadHardwareItems.initial),
        mergeMap(() => this.plasticsApiService.loadHardwareItems().pipe(
            map((hardwareItems) => PlasticsActions.LoadHardwareItems.success({ hardwareItems })),
            catchError((error) => of(PlasticsActions.LoadHardwareItems.failure({ error })))
        ))
    ));


    LoadHardwareItemById$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadHardwareItemById.initial),
        mergeMap(({ hardwareItemId }) => this.plasticsApiService.loadHardwareItemById(hardwareItemId).pipe(
            map((hardwareItem) => PlasticsActions.LoadHardwareItemById.success({ hardwareItem })),
            catchError((error) => of(PlasticsActions.LoadHardwareItemById.failure({ error })))
        ))
    ));

    LoadHardwareItemsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadHardwareItemsByIds.initial),
        mergeMap(({ hardwareItemIds }) => this.plasticsApiService.loadHardwareItemsByIds(hardwareItemIds).pipe(
            map((hardwareItems) => PlasticsActions.LoadHardwareItemsByIds.success({ hardwareItems })),
            catchError((error) => of(PlasticsActions.LoadHardwareItemsByIds.failure({ error })))
        ))
    ));

    LoadHardwareItemsByType$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadHardwareItemsByType.initial),
        mergeMap(({ hardwareItemType }) => this.plasticsApiService.loadHardwareItemsByType(hardwareItemType).pipe(
            map((hardwareItems) => PlasticsActions.LoadHardwareItemsByType.success({ hardwareItems })),
            catchError((error) => of(PlasticsActions.LoadHardwareItemsByType.failure({ error })))
        ))
    ));

    SetHardwareItem$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetHardwareItem.initial),
        this.appendTimestamp<PlasticsHardwareItem>(({ hardwareItem }) => hardwareItem),
        mergeMap((hardwareItem) => this.plasticsApiService.setHardwareItem(hardwareItem).pipe(
            map(() => PlasticsActions.SetHardwareItem.success({ hardwareItem })),
            catchError((error) => of(PlasticsActions.SetHardwareItem.failure({ error })))
        ))
    ));

    LoadProducts$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadProducts.initial),
        mergeMap(() => this.plasticsApiService.loadProducts().pipe(
            map((products) => PlasticsActions.LoadProducts.success({ products })),
            catchError((error) => of(PlasticsActions.LoadProducts.failure({ error })))
        ))
    ));

    LoadReleasedProductVersions$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadReleasedProductVersions.initial),
        mergeMap(() => this.plasticsApiService.loadReleasedProductVersions().pipe(
            map((releasedProductVersions) => PlasticsActions.LoadReleasedProductVersions.success({ releasedProductVersions })),
            catchError((error) => of(PlasticsActions.LoadReleasedProductVersions.failure({ error })))
        ))
    ));

    LoadReleasedAndFinishedProductVersions$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadReleasedAndFinishedProductVersions.initial),
        mergeMap(() => this.plasticsApiService.loadReleasedAndFinishedProductVersions().pipe(
            map((releasedAndFinishedProductVersions) => PlasticsActions.LoadReleasedAndFinishedProductVersions.success({ releasedAndFinishedProductVersions })),
            catchError((error) => of(PlasticsActions.LoadReleasedAndFinishedProductVersions.failure({ error })))
        ))
    ))
    LoadReleasedAndMoldedProductVersions$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadReleasedAndMoldedProductVersions.initial),
        mergeMap(() => this.plasticsApiService.loadReleasedAndMoldedProductVersions().pipe(
            map((releasedAndMoldedProductVersions) => PlasticsActions.LoadReleasedAndMoldedProductVersions.success({ releasedAndMoldedProductVersions })),
            catchError((error) => of(PlasticsActions.LoadReleasedAndMoldedProductVersions.failure({ error })))
        ))
    ))

    LoadReleasedAndDraftProductVersions$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadReleasedAndDraftProductVersions.initial),
        mergeMap(() => this.plasticsApiService.loadReleasedAndDraftProductVersions().pipe(
            map((releasedAndDraftProductVersions) => PlasticsActions.LoadReleasedAndDraftProductVersions.success({ releasedAndDraftProductVersions })),
            catchError((error) => of(PlasticsActions.LoadReleasedAndDraftProductVersions.failure({ error })))
        ))
    ))

    LoadProductById$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadProductById.initial),
        mergeMap(({ productId }) => this.plasticsApiService.loadProductById(productId).pipe(
            map((product) => PlasticsActions.LoadProductById.success({ product })),
            catchError((error) => of(PlasticsActions.LoadProductById.failure({ error })))
        ))
    ));

    LoadProductsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadProductsByIds.initial),
        mergeMap(({ productIds }) => this.plasticsApiService.loadProductsByIds(productIds).pipe(
            map((products) => PlasticsActions.LoadProductsByIds.success({ products })),
            catchError((error) => of(PlasticsActions.LoadProductsByIds.failure({ error })))
        ))
    ));

    LoadProductVersionById$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadProductVersionById.initial),
        mergeMap(({ productVersionId }) => this.plasticsApiService.loadProductVersionById(productVersionId).pipe(
            map((productVersion) => PlasticsActions.LoadProductVersionById.success({ productVersion })),
            catchError((error) => of(PlasticsActions.LoadProductVersionById.failure({ error })))
        ))
    ))

    LoadProductVersionsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadProductVersionsByIds.initial),
        mergeMap(({ productVersionIds }) => this.plasticsApiService.loadProductVersionsByIds(productVersionIds).pipe(
            map((productVersions) => PlasticsActions.LoadProductVersionsByIds.success({ productVersions })),
            catchError((error) => of(PlasticsActions.LoadProductVersionsByIds.failure({ error })))
        ))
    ))


    LoadProductVersionsByFixtureId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadProductVersionsByFixtureId.initial),
        mergeMap(({ fixtureId }) => this.plasticsApiService.loadProductVersionsByFixtureId(fixtureId).pipe(
            map((productVersions) => PlasticsActions.LoadProductVersionsByFixtureId.success({ productVersions })),
            catchError((error) => of(PlasticsActions.LoadProductVersionsByFixtureId.failure({ error })))
        ))
    ))

    LoadBillOfMaterialsLineItems$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadBillOfMaterialsLineItems.initial),
        mergeMap(() => this.plasticsApiService.loadBillOfMaterialsLineItems().pipe(
            map((lineItems) => PlasticsActions.LoadBillOfMaterialsLineItems.success({ lineItems })),
            catchError((error) => of(PlasticsActions.LoadBillOfMaterialsLineItems.failure({ error })))
        ))
    ))

    LoadBillOfMaterialsLineItemsByProductId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadBillOfMaterialsLineItemsByProductId.initial),
        mergeMap(({ productId }) => this.plasticsApiService.loadBillOfMaterialsLineItemsByProductVersionId(productId).pipe(
            map((lineItems) => PlasticsActions.LoadBillOfMaterialsLineItemsByProductId.success({ lineItems })),
            catchError((error) => of(PlasticsActions.LoadBillOfMaterialsLineItemsByProductId.failure({ error })))
        ))
    ));

    LoadBillOfMaterialsLineItemsByProductVersionId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadBillOfMaterialsLineItemsByProductVersionId.initial),
        mergeMap(({ productVersionId }) => this.plasticsApiService.loadBillOfMaterialsLineItemsByProductVersionId(productVersionId).pipe(
            map((lineItems) => PlasticsActions.LoadBillOfMaterialsLineItemsByProductVersionId.success({ lineItems })),
            catchError((error) => of(PlasticsActions.LoadBillOfMaterialsLineItemsByProductVersionId.failure({ error })))
        ))
    ))

    SetBillOfMaterialsLineItems$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetBillOfMaterialsLineItems.initial),
        mergeMap(({ lineItems }) => this.plasticsApiService.setBillOfMaterialsLineItems(lineItems).pipe(
            map(() => PlasticsActions.SetBillOfMaterialsLineItems.success({ lineItems })),
            catchError((error) => of(PlasticsActions.SetBillOfMaterialsLineItems.failure({ error })))
        ))
    ));

    SetMappingProductVersionToQualityAlerts$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetMappingProductVersionToQualityAlerts.initial),
        mergeMap(({ mappings }) => this.plasticsApiService.setMappingProductVersionToQualityAlerts(mappings).pipe(
            map(() => PlasticsActions.SetMappingProductVersionToQualityAlerts.success({ mappings })),
            catchError((error) => of(PlasticsActions.SetMappingProductVersionToQualityAlerts.failure({ error })))
        ))
    ));

    SetMappingProductVersionToDockInspectionQuailtyAlert$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetMappingProductVersionToDockInspectionQuailtyAlert.initial),
        mergeMap(({ mappings }) => this.plasticsApiService.setMappingProductVersionToDockInspectionsQualityAlerts(mappings).pipe(
            map(() => PlasticsActions.SetMappingProductVersionToDockInspectionQuailtyAlert.success({ mappings })),
            catchError((error) => of(PlasticsActions.SetMappingProductVersionToDockInspectionQuailtyAlert.failure({ error })))
        ))
    ));

    SetMappingComplaintLogToQualityAlerts$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetMappingComplaintLogToQualityAlerts.initial),
        mergeMap(({ mappingComplaintLogToQualityAlert }) => this.plasticsApiService.setMappingComplaintLogToQualityAlerts(mappingComplaintLogToQualityAlert).pipe(
            map(() => PlasticsActions.SetMappingComplaintLogToQualityAlerts.success({ mappingComplaintLogToQualityAlert })),
            catchError((error) => of(PlasticsActions.SetMappingComplaintLogToQualityAlerts.failure({ error })))
        ))
    ));

    DeleteMappingComplaintLogToQualityAlerts$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeleteMappingComplaintLogToQualityAlerts.initial),
        mergeMap(({ qualityAlertIds, complaintLogId }) => this.plasticsApiService.deleteMappingComplaintLogToQualityAlerts(qualityAlertIds, complaintLogId).pipe(
            map(() => PlasticsActions.DeleteMappingComplaintLogToQualityAlerts.success({ qualityAlertIds })),
            catchError((error) => {
                console.log(error);
                return of(PlasticsActions.DeleteMappingComplaintLogToQualityAlerts.failure({ error }))
            })
        ))
    ));

    DeleteMappingsComplaintLogToContacts$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeleteMappingsComplaintLogToContacts.initial),
        mergeMap(({ contactIds, complaintLogId }) => this.plasticsApiService.deleteMappingsComplaintLogToContacts(contactIds, complaintLogId).pipe(
            map(() => PlasticsActions.DeleteMappingsComplaintLogToContacts.success({ contactIds })),
            catchError((error) => {
                console.log(error);
                return of(PlasticsActions.DeleteMappingsComplaintLogToContacts.failure({ error }))
            })
        ))
    ));


    LoadContacts$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadContacts.initial),
        mergeMap(() => this.plasticsApiService.loadContacts().pipe(
            map((contacts) => PlasticsActions.LoadContacts.success({ contacts })),
            catchError((error) => of(PlasticsActions.LoadContacts.failure({ error })))
        ))
    ));

    LoadContactById$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadContactById.initial),
        mergeMap(({ contactId }) => this.plasticsApiService.loadContactById(contactId).pipe(
            map((contact) => PlasticsActions.LoadContactById.success({ contact })),
            catchError((error) => of(PlasticsActions.LoadContactById.failure({ error })))
        ))
    ));

    SetContact$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetContact.initial),
        // Need to add correct timestamps to customer
        // this.appendTimestamp<MetalsCustomer>(({customer}) => customer),
        mergeMap(({ contact }) => this.plasticsApiService.setContact(contact).pipe(
            map(() => PlasticsActions.SetContact.success({ contact })),
            catchError((error) => of(PlasticsActions.SetContact.failure({ error })))
        ))
    ));

    SetVendorHardwareDataAndHardwareItem$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetVendorHardwareDataAndHardwareItem.initial),
        // need to append timestamps
        mergeMap(({ vendorHardwareData, hardwareItem }) => forkJoin([
            this.plasticsApiService.setVendorHardwareData(vendorHardwareData),
            this.plasticsApiService.setHardwareItem(hardwareItem),
        ]).pipe(
            map(() => PlasticsActions.SetVendorHardwareDataAndHardwareItem.success({ hardwareItem, vendorHardwareData })),
            catchError((error) => of(PlasticsActions.SetVendorHardwareDataAndHardwareItem.failure({ error })))
        ))
    ));

    SetVendorHardwareData$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetVendorHardwareData.initial),
        this.appendTimestamp<VendorHardwareData>(({ vendorHardwareData }) => vendorHardwareData),
        mergeMap((vendorHardwareData) => this.plasticsApiService.setVendorHardwareData(vendorHardwareData).pipe(
            map(() => PlasticsActions.SetVendorHardwareData.success({ vendorHardwareData })),
            catchError((error) => of(PlasticsActions.SetVendorHardwareData.failure({ error })))
        ))
    ));

    LoadVendorHardwareData$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadVendorHardwareData.initial),
        mergeMap(() => this.plasticsApiService.loadVendorHardwareData().pipe(
            map((vendorHardwareData) => PlasticsActions.LoadVendorHardwareData.success({ vendorHardwareData })),
            catchError((error) => of(PlasticsActions.LoadVendorHardwareData.failure({ error })))
        ))
    ));

    LoadTags$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadTags.initial),
        mergeMap(() => this.plasticsApiService.loadTags().pipe(
            map((tags) => PlasticsActions.LoadTags.success({ tags })),
            catchError((error) => of(PlasticsActions.LoadTags.failure({ error })))
        ))
    ));

    LoadVendorHardwareDataByHardwareItemId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadVendorHardwareDataByHardwareItemId.initial),
        mergeMap(({ hardwareItemId }) => this.plasticsApiService.loadVendorHardwareDataByHardwareItemId(hardwareItemId).pipe(
            map((vendorHardwareData) => PlasticsActions.LoadVendorHardwareDataByHardwareItemId.success({ vendorHardwareData })),
            catchError((error) => of(PlasticsActions.LoadVendorHardwareDataByHardwareItemId.failure({ error })))
        ))
    ));

    LoadVendorHardwareDataByHardwareItemIds$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadVendorHardwareDataByHardwareItemIds.initial),
        mergeMap(({ hardwareItemIds }) => this.plasticsApiService.loadVendorHardwareDataByHardwareItemIds(hardwareItemIds).pipe(
            map((vendorHardwareData) => PlasticsActions.LoadVendorHardwareDataByHardwareItemIds.success({ vendorHardwareData })),
            catchError((error) => of(PlasticsActions.LoadVendorHardwareDataByHardwareItemIds.failure({ error })))
        ))
    ));

    SetHardwareInventoryTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlasticsActions.SetHardwareInventoryTransaction.initial),
            withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
            mergeMap(([{ hardwareId, qtyChange, comment }, curentUser]) => this.plasticsApiService.newHardwareInventoryTransaction(
                hardwareId,
                qtyChange,
                comment,
                curentUser
            ).pipe(
                map((transaction: InventoryTransaction) => PlasticsActions.SetHardwareInventoryTransaction.success({ transaction })),
                catchError((error) => of(PlasticsActions.SetHardwareInventoryTransaction.failure({ error })))
            ))
        )
    )

    SetProductInventoryTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlasticsActions.SetProductInventoryTransaction.initial),
            withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
            mergeMap(([
                {
                    productId,
                    qtyChange,
                    comment,
                    hardwareIdToQuantityChangeDict,
                    productIdToQuantityChangeDict,
                    materialIdToQuantityChangeDict
                },
                curentUser
            ]) => this.plasticsApiService.newProductInventoryTransaction(
                productId,
                qtyChange,
                comment,
                hardwareIdToQuantityChangeDict,
                productIdToQuantityChangeDict,
                materialIdToQuantityChangeDict,
                curentUser
            ).pipe(
                map((transaction: InventoryTransaction) => PlasticsActions.SetProductInventoryTransaction.success({ transaction })),
                catchError((error) => of(PlasticsActions.SetProductInventoryTransaction.failure({ error })))
            ))
        )
    )

    SetMaterialInventoryTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlasticsActions.SetMaterialInventoryTransaction.initial),
            withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
            mergeMap(([
                {
                    materialId,
                    qtyChange,
                    comment,
                    hardwareIdToQuantityChangeDict,
                },
                curentUser
            ]) => this.plasticsApiService.newMaterialInventoryTransaction(
                materialId,
                qtyChange,
                comment,
                hardwareIdToQuantityChangeDict,
                curentUser
            ).pipe(
                map((transaction) => PlasticsActions.SetMaterialInventoryTransaction.success({ transaction })),
                catchError((error) => of(PlasticsActions.SetMaterialInventoryTransaction.failure({ error })))
            ))
        )
    )

    LoadRecentHardwareInventoryTransactions$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadRecentHardwareInventoryTransactions.initial),
        mergeMap(() => this.plasticsApiService.loadRecentHardwareInventoryTransactions().pipe(
            map((transactions) => PlasticsActions.LoadRecentHardwareInventoryTransactions.success({ transactions })),
            catchError((error) => of(PlasticsActions.LoadRecentHardwareInventoryTransactions.failure({ error })))
        ))
    ));

    LoadRecentProductInventoryTransactions$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadRecentProductInventoryTransactions.initial),
        mergeMap(() => this.plasticsApiService.loadRecentProductInventoryTransactions().pipe(
            map((transactions) => PlasticsActions.LoadRecentProductInventoryTransactions.success({ transactions })),
            catchError((error) => of(PlasticsActions.LoadRecentProductInventoryTransactions.failure({ error })))
        ))
    ));

    LoadRecentMaterialInventoryTransactions$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadRecentMaterialInventoryTransactions.initial),
        mergeMap(() => this.plasticsApiService.loadRecentMaterialnventoryTransactions().pipe(
            map((transactions) => PlasticsActions.LoadRecentMaterialInventoryTransactions.success({ transactions })),
            catchError((error) => of(PlasticsActions.LoadRecentMaterialInventoryTransactions.failure({ error })))
        ))
    ));

    LoadRotoSchedule$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadRotoSchedule.initial),
        mergeMap(() => this.plasticsApiService.loadRotoSchedule().pipe(
            map((rotoSchedule) => PlasticsActions.LoadRotoSchedule.success({ rotoSchedule })),
            catchError((error) => of(PlasticsActions.LoadRotoSchedule.failure({ error })))
        ))
    ));

    SetRotoSchedule$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetRotoSchedule.initial),
        mergeMap(({ rotoSchedule }) => this.plasticsApiService.setRotoSchedule(rotoSchedule).pipe(
            switchMap(() => this.plasticsApiService.loadRotoSchedule()),
            mergeMap(saved => [PlasticsActions.LoadRotoSchedule.set({ rotoSchedule: saved }), PlasticsActions.SetRotoSchedule.success({ rotoSchedule: saved })]),
            catchError((error) => this.plasticsApiService.loadRotoSchedule().pipe(
                mergeMap(saved => [PlasticsActions.LoadRotoSchedule.set({ rotoSchedule: saved }), PlasticsActions.SetRotoSchedule.failure({ error })]),
                catchError(() => of(PlasticsActions.SetRotoSchedule.failure({ error }))),
            ))
        ))
    ));

    LoadRotoMachines$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadRotoMachines.initial),
        mergeMap(() => this.plasticsApiService.loadRotoMachines().pipe(
            map((rotoMachines) => PlasticsActions.LoadRotoMachines.success({ rotoMachines })),
            catchError((error) => of(PlasticsActions.LoadRotoMachines.failure({ error })))
        ))
    ));

    loadRotoMachineById$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadRotoMachineById.initial),
        mergeMap(({ rotoMachineId }) => this.plasticsApiService.loadRotoMachineById(rotoMachineId).pipe(
            map((rotoMachine) => PlasticsActions.LoadRotoMachineById.success({ rotoMachine })),
            catchError((error) => of(PlasticsActions.LoadRotoMachineById.failure({ error })))
        ))
    ));

    setRotoMachine$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetRotoMachine.initial),
        mergeMap(({ machine }) => this.plasticsApiService.setRotoMachine(machine).pipe(
            map(() => PlasticsActions.SetRotoMachine.success({ machine })),
            catchError((error) => of(PlasticsActions.SetRotoMachine.failure({ error })))
        ))
    ));

    LoadMoldingTasksByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadMoldingTasksByIds.initial),
        mergeMap(({ moldingTaskIds: scheduledProductIds }) => this.plasticsApiService.loadMoldingTasksByIds(scheduledProductIds).pipe(
            map((scheduledProducts) => PlasticsActions.LoadMoldingTasksByIds.success({ moldingTasks: scheduledProducts })),
            catchError((error) => of(PlasticsActions.LoadMoldingTasksByIds.failure({ error })))
        ))
    ));

    SetMoldingTask$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetMoldingTask.initial),
        mergeMap(({ moldingTask: scheduledProduct }) => this.plasticsApiService.setMoldingTask(scheduledProduct).pipe(
            map(() => PlasticsActions.SetMoldingTask.success({ moldingTask: scheduledProduct })),
            catchError((error) => of(PlasticsActions.SetMoldingTask.failure({ error })))
        ))
    ));

    SetMoldingTasks$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetMoldingTasks.initial),
        mergeMap(({ tasks }) => this.plasticsApiService.setMoldingTasks(tasks).pipe(
            map(() => PlasticsActions.SetMoldingTasks.success({ tasks })),
            catchError((error) => of(PlasticsActions.SetMoldingTasks.failure({ error })))
        ))
    ));

    SetFinishingTasks$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetFinishingTasks.initial),
        mergeMap(({ tasks }) => this.plasticsApiService.setFinishingTasks(tasks).pipe(
            map(() => PlasticsActions.SetFinishingTasks.success({ tasks })),
            catchError((error) => of(PlasticsActions.SetFinishingTasks.failure({ error })))
        ))
    ));

    LoadFixtures$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadFixtures.initial),
        mergeMap(() => this.plasticsApiService.loadFixtures().pipe(
            map((fixtures) => PlasticsActions.LoadFixtures.success({ fixtures })),
            catchError((error) => of(PlasticsActions.LoadFixtures.failure(error)))
        ))
    ));

    SetFixture$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetFixture.initial),
        mergeMap(({ fixture }) => this.plasticsApiService.setFixture(fixture).pipe(
            map(() => PlasticsActions.SetFixture.success({ fixture })),
            catchError((error) => of(PlasticsActions.SetFixture.failure({ error })))
        ))
    ));

    LoadFixtureById$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadFixtureById.initial),
        mergeMap(({ fixtureId }) => this.plasticsApiService.loadFixtureById(fixtureId).pipe(
            map((fixture) => PlasticsActions.LoadFixtureById.success({ fixture })),
            catchError((error) => of(PlasticsActions.LoadFixtureById.failure({ error })))
        ))
    ));

    LoadFixturesByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadFixturesByIds.intitial),
        mergeMap(({ fixtureIds }) => this.plasticsApiService.loadFixturesByIds(fixtureIds).pipe(
            map((fixtures) => PlasticsActions.LoadFixturesByIds.success({ fixtures })),
            catchError((error) => of(PlasticsActions.LoadFixturesByIds.failure({ error })))
        ))
    ))

    LoadScrapReasons$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadScrapReasons.initial),
        mergeMap(() => this.plasticsApiService.loadScrapReasons().pipe(
            map((scrapReasons) => PlasticsActions.LoadScrapReasons.success({ scrapReasons })),
            catchError((error) => of(PlasticsActions.LoadScrapReasons.failure({ error })))
        ))
    ))

    GetScrapInstancesByIdFromDate$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.GetPlasticsScrapInstancesByIdFromDate.initial),
        mergeMap(({ productId, date }) => this.plasticsApiService.loadScrapInstancesByIdFromDate(productId, date).pipe(
            map((plasticsScrapInstances) => PlasticsActions.GetPlasticsScrapInstancesByIdFromDate.success({ plasticsScrapInstances })),
            catchError((error) => of(PlasticsActions.GetPlasticsScrapInstancesByIdFromDate.failure({ error })))
        ))
    ))


    SetProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlasticsActions.SetProduct.initial),
            mergeMap(({ product }) => this.plasticsApiService.setProduct(product).pipe(
                map(() => PlasticsActions.SetProduct.success({ product })),
                catchError((error) => of(PlasticsActions.SetProduct.failure({ error })))
            ))
        )
    )

    SetProductVersion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlasticsActions.SetProductVersion.initial),
            mergeMap(({ productVersion }) => this.plasticsApiService.setProductVersion(productVersion).pipe(
                map(() => PlasticsActions.SetProductVersion.success({ productVersion })),
                catchError((error) => of(PlasticsActions.SetProductVersion.failure({ error })))
            ))
        )
    )

    SetProductVersions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PlasticsActions.SetProductVersions.initial),
            mergeMap(({ productVersions }) => this.plasticsApiService.setProductVersions(productVersions).pipe(
                map(() => PlasticsActions.SetProductVersions.success({ productVersions })),
                catchError((error) => of(PlasticsActions.SetProductVersions.failure({ error })))
            ))
        )
    )

    // =====================
    RecordScheduledProductArmCycle$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.RecordScheduledProductArmCycle.initial),
        withLatestFrom(
            this.store.select(CoreSelectors.getCurrentUser),
        ),
        mergeMap(([
            { productInstances, machineId, armId },
            currentUser
        ]) => {

            return this.plasticsApiService.recordRotoMachineArmCycle(
                productInstances,
                machineId,
                armId,
                currentUser,
            ).pipe(
                map((moldingTasks) => PlasticsActions.RecordScheduledProductArmCycle.success({ moldingTasks })),
                catchError((error) => of(PlasticsActions.RecordScheduledProductArmCycle.failure({ error })))
            )

        })
    ));

    LoadMoldingTaskDependantDataByMoldingTaskIds$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadMoldingTaskDependantDataByMoldingTaskIds.initial),
        switchMap(({ moldingTaskIds }) => {
            const uniqueMoldingTaskIds = [...new Set(moldingTaskIds.filter(id => !!id))];
            if (uniqueMoldingTaskIds.length === 0) {
                return of(PlasticsActions.LoadMoldingTaskDependantDataByMoldingTaskIds.success({
                    moldingTasks: [],
                    products: [],
                    productVersions: [],
                    hardwareItems: [],
                    materials: [],
                    billOfMaterialsLineItems: [],
                    qualityAlerts: [],
                    mappingProductVersionToQualityAlerts: [],
                    mappingPlasticsMaterialToQualityAlerts: [],
                }));
            }

            return this.plasticsApiService.loadMoldingTasksByIds(uniqueMoldingTaskIds).pipe(
                mergeMap((moldingTasks) => {
                    const productIds = [...new Set(moldingTasks
                        .map(mt => mt.productId)
                        .filter(mt => !!mt))]
                    return this.plasticsApiService.loadProductsAndReleasedProductVersionsByIds(productIds).pipe(
                        mergeMap(({ products, productVersions: releasedProductVersions }) => {
                            const releasedProductVersionIds = [...new Set(releasedProductVersions.map(pv => pv.id))]
                            return this.plasticsApiService.loadBillOfMaterialsLineItemsByProductVersionIds(releasedProductVersionIds).pipe(
                                mergeMap((lineItems) => {
                                    const hardwareItemIds = [...new Set(lineItems.filter(li => li.type === BillOfMaterialsLineItemType.Hardware).map(li => li.entityId))];
                                    const productIds = [...new Set(lineItems.filter(li => li.type === BillOfMaterialsLineItemType.Product).map(li => li.entityId))];
                                    const materialIds = [...new Set(lineItems.filter(li => li.type === BillOfMaterialsLineItemType.Material).map(li => li.entityId))];
                                    return this.plasticsApiService.loadProductsAndReleasedProductVersionsByIds(productIds).pipe(
                                        mergeMap(({ products: billOfMaterialsProducts, productVersions: billOfMaterialsProductVersions }) => {
                                            const allProducts = MergeArrayValues(products, billOfMaterialsProducts);
                                            const allProductVersions = MergeArrayValues(releasedProductVersions, billOfMaterialsProductVersions);

                                            const productVersionIds = [...new Set(allProductVersions.map(v => v.id))]
                                            const mappingProductVersionToQualityAlerts$ = this.plasticsApiService.loadMappingProductVersionToQualityAlertsByProductVersionIds(productVersionIds);
                                            const mappingPlasticsMaterialToQualityAlerts$ = this.plasticsApiService.loadMappingMaterialToQualityAlertsByMaterialIds(materialIds);

                                            return forkJoin([
                                                mappingProductVersionToQualityAlerts$,
                                                mappingPlasticsMaterialToQualityAlerts$,
                                                hardwareItemIds.length ? this.plasticsApiService.loadHardwareItemsByIds(hardwareItemIds) : of([])
                                            ]).pipe(
                                                mergeMap(([mappingProductVersionToQualityAlerts, mappingPlasticsMaterialToQualityAlerts, hardwareItems]) => {
                                                    const qualityAlertIds = [...new Set([
                                                        ...mappingProductVersionToQualityAlerts.map(mapping => mapping.qualityAlertId),
                                                        ...mappingPlasticsMaterialToQualityAlerts.map(mapping => mapping.qualityAlertId),
                                                    ])];
                                                    const qualityAlerts$ = qualityAlertIds.length
                                                        ? this.plasticsApiService.loadPlasticsQualityAlertsByIds(qualityAlertIds)
                                                        : of([]);

                                                    return forkJoin([
                                                        qualityAlerts$,
                                                        materialIds.length ? this.plasticsApiService.loadMaterialsByIds(materialIds) : of([]),
                                                    ]).pipe(
                                                        mergeMap(([allQualityAlerts, materials]) => {
                                                            const resourceIds = [...new Set(releasedProductVersions
                                                                .map(pv => pv.drawingResourceId)
                                                                .filter(resourceId => !!resourceId))]
                                                            return [
                                                                PlasticsActions.LoadMoldingTaskDependantDataByMoldingTaskIds.success({
                                                                    moldingTasks,
                                                                    products: allProducts,
                                                                    productVersions: allProductVersions,
                                                                    hardwareItems,
                                                                    materials,
                                                                    billOfMaterialsLineItems: lineItems,
                                                                    qualityAlerts: allQualityAlerts,
                                                                    mappingProductVersionToQualityAlerts,
                                                                    mappingPlasticsMaterialToQualityAlerts,
                                                                }),
                                                                PrismCommonActions.LoadResourcesByIds.initial({ resourceIds })
                                                            ]
                                                        }),
                                                        catchError(({ error }) => of(PlasticsActions.LoadMoldingTaskDependantDataByMoldingTaskIds.failure({ error: 'BOM materials failed ' + error })))
                                                    )

                                                }),
                                                catchError(({ error }) => of(PlasticsActions.LoadMoldingTaskDependantDataByMoldingTaskIds.failure({ error: 'BOM hardware items failed ' + error })))
                                            )
                                        }),
                                        catchError(({ error }) => of(PlasticsActions.LoadMoldingTaskDependantDataByMoldingTaskIds.failure({ error: 'BOM product & released versions failed ' + error })))
                                    )
                                }),
                                catchError(({ error }) => of(PlasticsActions.LoadMoldingTaskDependantDataByMoldingTaskIds.failure({ error: 'BOM line items failed ' + error })))
                            )
                        }),
                        catchError(({ error }) => of(PlasticsActions.LoadMoldingTaskDependantDataByMoldingTaskIds.failure({ error: 'product & released versions failed ' + error })))
                    )
                }),
                catchError(({ error }) => of(PlasticsActions.LoadMoldingTaskDependantDataByMoldingTaskIds.failure({ error: 'scheduled products failed ' + error })))
            )
        }),
        catchError(({ error }) => of(PlasticsActions.LoadMoldingTaskDependantDataByMoldingTaskIds.failure({ error })))
    ));

    LoadResourcesReferencedByProductVersions$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadProductAndProductVersionsByDrawingResourceId.initial),
        mergeMap(({ resourceId }) => this.plasticsApiService.loadProductAndProductVersionsByResourceId(resourceId).pipe(
            map(({ products, productVersions }) => PlasticsActions.LoadProductAndProductVersionsByDrawingResourceId.success({ products, productVersions })),
            catchError(({ error }) => of(PlasticsActions.LoadProductAndProductVersionsByDrawingResourceId.failure({ error })))
        ))
    ));

    LoadHardwareItemsByDrawingResourceId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadHardwareItemsByDrawingResourceId.initial),
        mergeMap(({ resourceId }) => this.plasticsApiService.loadHardwareItemBySafetyDataSheetResourceId(resourceId).pipe(
            map((hardwareItems) => PlasticsActions.LoadHardwareItemsByDrawingResourceId.success({ hardwareItems })),
            catchError(({ error }) => of(PlasticsActions.LoadHardwareItemsByDrawingResourceId.failure({ error })))
        ))
    ));


    SetMaterial$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetMaterial.initial),
        mergeMap(({ material }) => this.plasticsApiService.setMaterial(material).pipe(
            map(() => PlasticsActions.SetMaterial.success({ material })),
            catchError(({ error }) => of(PlasticsActions.SetMaterial.failure({ error })))
        ))
    ));

    LoadMaterials$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadMaterials.initial),
        mergeMap(() => this.plasticsApiService.loadMaterials().pipe(
            map((materials) => PlasticsActions.LoadMaterials.success({ materials })),
            catchError(({ error }) => of(PlasticsActions.LoadMaterials.failure({ error })))
        ))
    ));

    LoadMaterialById$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadMaterialById.initial),
        mergeMap(({ materialId }) => this.plasticsApiService.loadMaterialById(materialId).pipe(
            map((material) => PlasticsActions.LoadMaterialById.success({ material })),
            catchError(({ error }) => of(PlasticsActions.LoadMaterialById.failure({ error })))
        ))
    ));

    LoadMaterialByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadMaterialsByIds.initial),
        mergeMap(({ materialIds }) => this.plasticsApiService.loadMaterialsByIds(materialIds).pipe(
            map((materials) => PlasticsActions.LoadMaterialsByIds.success({ materials })),
            catchError(({ error }) => of(PlasticsActions.LoadMaterialsByIds.failure({ error })))
        ))
    ));

    LoadMaterialInventoryTransactionsByMaterialId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadMaterialInventoryTransactionsByMaterialId.initial),
        mergeMap(({ materialId }) => this.plasticsApiService.loadMaterialInventoryTransactionsByMaterialId(materialId).pipe(
            map((transactions) => PlasticsActions.LoadMaterialInventoryTransactionsByMaterialId.success({ transactions })),
            catchError(({ error }) => {
                return of(PlasticsActions.LoadMaterialInventoryTransactionsByMaterialId.failure({ error }))
            })
        ))
    ));

    LoadMaterialsByIngredientHardwareItemId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadMaterialsByIngredientHardwareId.initial),
        mergeMap(({ hardwareItemId }) => this.plasticsApiService.loadMaterials().pipe(
            map((materials) => materials.filter(material => material.ingredients.some(ingredient => ingredient.hardwareItemId === hardwareItemId))),
            map((materials) => PlasticsActions.LoadMaterialsByIngredientHardwareId.success({ materials })),
            catchError((error) => of(PlasticsActions.LoadMaterialsByIngredientHardwareId.failure({ error })))
        ))
    ))


    SetMaterialMixingTask$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetMaterialMixingTask.initial),
        mergeMap(({ task }) => this.plasticsApiService.setMaterialMixingTask(task).pipe(
            map(() => PlasticsActions.SetMaterialMixingTask.success({ task })),
            catchError(({ error }) => of(PlasticsActions.SetMaterialMixingTask.failure({ error })))
        ))
    ));

    SetMaterialMixingTasks$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetMaterialMixingTasks.initial),
        mergeMap(({ tasks }) => this.plasticsApiService.setMaterialMixingTasks(tasks).pipe(
            map(() => PlasticsActions.SetMaterialMixingTasks.success({ tasks })),
            catchError((error) => of(PlasticsActions.SetMaterialMixingTasks.failure({ error })))
        ))
    ))

    DeleteMaterialMixingTask$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeleteMaterialMixingTask.initial),
        mergeMap(({ taskId }) => this.plasticsApiService.deleteMaterialMixingTask(taskId).pipe(
            map(() => PlasticsActions.DeleteMaterialMixingTask.success({ taskId })),
            catchError((error) => of(PlasticsActions.DeleteMaterialMixingTask.failure({ error })))
        ))
    ))

    SetPurchaseRequisition$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetPurchaseRequisition.initial),
        mergeMap(({ requisition }) => this.plasticsApiService.setPurchaseRequisition(requisition).pipe(
            map(() => PlasticsActions.SetPurchaseRequisition.success({ requisition })),
            catchError((error) => of(PlasticsActions.SetPurchaseRequisition.failure({ error })))
        ))
    ))

    SetPurchaseRequisitions$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetPurchaseRequisitions.initial),
        mergeMap(({ requisitions }) => this.plasticsApiService.setPurchaseRequisitions(requisitions).pipe(
            map(() => PlasticsActions.SetPurchaseRequisitions.success({ requisitions })),
            catchError((error) => of(PlasticsActions.SetPurchaseRequisitions.failure({ error })))
        ))
    ))

    DeletePurchaseRequisition$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeletePurchaseRequisition.initial),
        mergeMap(({ requisitionId }) => this.plasticsApiService.deletePurchaseRequisition(requisitionId).pipe(
            map(() => PlasticsActions.DeletePurchaseRequisition.success({ requisitionId })),
            catchError((error) => of(PlasticsActions.DeletePurchaseRequisition.failure({ error })))
        ))
    ))

    DeletePurchaseRequisitions$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeletePurchaseRequisitions.initial),
        mergeMap(({ requisitionIds }) => this.plasticsApiService.deletePurchaseRequisitions(requisitionIds).pipe(
            map(() => PlasticsActions.DeletePurchaseRequisitions.success({ requisitionIds })),
            catchError((error) => of(PlasticsActions.DeletePurchaseRequisitions.failure({ error })))
        ))
    ))

    LoadStaticLabels$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadPlasticsStaticLabels.initial),
        mergeMap(() => this.plasticsApiService.loadStaticLabels().pipe(
            map((plasticsStaticLabels) => PlasticsActions.LoadPlasticsStaticLabels.success({ plasticsStaticLabels })),
            catchError(({ error }) => of(PlasticsActions.LoadPlasticsStaticLabels.failure({ error })))
        ))
    ));

    LoadPlasticsQualityAlerts$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadPlasticsQualityAlerts.initial),
        mergeMap(() => this.plasticsApiService.loadPlasticsQualityAlerts().pipe(
            map((plasticsQualityAlerts) => PlasticsActions.LoadPlasticsQualityAlerts.success({ plasticsQualityAlerts })),
            catchError(({ error }) => of(PlasticsActions.LoadPlasticsQualityAlerts.failure({ error })))
        ))
    ));

    SetPlasticsQualityAlertInspection$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetPlasticsQualityAlertInspection.initial),
        mergeMap(({ inspection }) => this.plasticsApiService.setPlasticsQualityAlertInspection(inspection).pipe(
            map(() => PlasticsActions.SetPlasticsQualityAlertInspection.success({ inspection })),
            catchError(({ error }) => of(PlasticsActions.SetPlasticsQualityAlertInspection.failure({ error })))
        ))
    ))

    LoadMappingProductVersionToQualityAlertAndReleasedProductVersions$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadMappingProductVersionToQualityAlertAndProductVersions.initial),
        mergeMap(() => this.plasticsApiService.loadMappingProductVersionToQualityAlerts().pipe(
            mergeMap((mappingProductVersionToQualityAlerts) => {
                const productVersionIds = [...new Set(mappingProductVersionToQualityAlerts.map(m => m.productVersionId))];
                return this.plasticsApiService.loadProductVersionsByIds(productVersionIds).pipe(
                    map((productVersions) => PlasticsActions.LoadMappingProductVersionToQualityAlertAndProductVersions.success({ productVersions, mappingProductVersionToQualityAlerts })),
                    catchError(({ error }) => of(PlasticsActions.LoadMappingProductVersionToQualityAlertAndProductVersions.failure({ error })))
                )
            }),
            catchError(({ error }) => of(PlasticsActions.LoadMappingProductVersionToQualityAlertAndProductVersions.failure({ error })))
        ))
    ));

    LoadMappingProductVersionToQualityAlertAndQualityAlerts$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadMappingProductVersionToQualityAlertAndQualityAlerts.initial),
        mergeMap(() => this.plasticsApiService.loadMappingProductVersionToQualityAlerts().pipe(
            mergeMap((mappingProductVersionToQualityAlerts) => {
                const qualityAlertIds = [...new Set(mappingProductVersionToQualityAlerts.map(m => m.qualityAlertId))];
                return this.plasticsApiService.loadPlasticsQualityAlertsByIds(qualityAlertIds).pipe(
                    map((qualityAlerts) => PlasticsActions.LoadMappingProductVersionToQualityAlertAndQualityAlerts.success({ qualityAlerts, mappingProductVersionToQualityAlerts })),
                    catchError(({ error }) => of(PlasticsActions.LoadMappingProductVersionToQualityAlertAndQualityAlerts.failure({ error })))
                )
            }),
            catchError(({ error }) => of(PlasticsActions.LoadMappingProductVersionToQualityAlertAndQualityAlerts.failure({ error })))
        ))
    ));

    LoadPlasticsQualityAlertInspectionChecklistData$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadPlasticsQualityAlertInspectionChecklistData.initial),
        mergeMap(() => forkJoin([
            this.plasticsApiService.loadMappingProductVersionToQualityAlerts(),
            this.plasticsApiService.loadPlasticsQualityAlerts(),
            this.plasticsApiService.loadRotoMachines(),
            this.plasticsApiService.loadReleasedProductVersions(),
            this.plasticsApiService.loadPlasticsQualityAlertInspections(),
        ]).pipe(
            map(([
                mappingProductVersionToQualityAlerts,
                qualityAlerts,
                rotoMachines,
                releasedProductVersions,
                qualityAlertInspections,
            ]) => {
                return PlasticsActions.LoadPlasticsQualityAlertInspectionChecklistData.success({
                    mappingProductVersionToQualityAlerts,
                    qualityAlerts,
                    rotoMachines,
                    releasedProductVersions,
                    qualityAlertInspections,
                })
            }),
            catchError(({ error }) => of(PlasticsActions.LoadPlasticsQualityAlertInspectionChecklistData.failure({ error })))
        ))
    ));


    // 0510713

    LoadBrowsePlasticsProductsQualityAlertData$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadBrowsePlasticsProductsQualityAlertData.initial),
        mergeMap(() => forkJoin([
            this.plasticsApiService.loadMappingProductVersionToQualityAlerts(),
            this.plasticsApiService.loadPlasticsQualityAlerts(),
            this.plasticsApiService.loadRotoSchedule(),
            this.plasticsApiService.loadRotoMachines(),
            this.plasticsApiService.loadReleasedProductVersions(),
        ]).pipe(
            mergeMap(([
                mappingProductVersionToQualityAlerts,
                qualityAlerts,
                rotoSchedule,
                rotoMachines,
                releasedProductVersions,
            ]) => {
                const scheduledProductIds = rotoSchedule.productLists.map(list => list.moldingTaskIds[0]);
                return this.plasticsApiService.loadMoldingTasksByIds(scheduledProductIds).pipe(
                    // map(versions => versions.filter(v => !!v)),
                    map((moldingTasks) => {
                        // console.log('releasedProductVersions', releasedProductVersions)
                        return PlasticsActions.LoadBrowsePlasticsProductsQualityAlertData.success({
                            mappingProductVersionToQualityAlerts,
                            qualityAlerts,
                            rotoMachines,
                            moldingTasks,
                            releasedProductVersions,
                            rotoSchedule
                        })
                    }),
                    catchError(({ error }) => of(PlasticsActions.LoadBrowsePlasticsProductsQualityAlertData.failure({ error })))
                    // map((scheduledProducts) => {

                    // const versionIds = [...new Set([
                    //     ...scheduledProducts.map(sp => sp.id),
                    //     ...mappingProductVersionToQualityAlerts.map(m => m.productVersionId)
                    // ])]
                    // const releasedProductVersions$ = versionIds
                    //     .map(productId => this.plasticsApiService.loadReleasedProductVersionByProductId(productId))


                    // return forkJoin(releasedProductVersions$).pipe(
                    // map(versions => versions.filter(v => !!v)),
                    // map((releasedProductVersions) => {
                    //     console.log('releasedProductVersions',releasedProductVersions)
                    //     return PlasticsActions.LoadBrowsePlasticsProductsQualityAlertData.success({
                    //         mappingProductVersionToQualityAlerts,
                    //         qualityAlerts,
                    //         rotoMachines,
                    //         scheduledProducts,
                    //         releasedProductVersions
                    //     })
                    // }),
                    // catchError(({ error }) => of(PlasticsActions.LoadBrowsePlasticsProductsQualityAlertData.failure({ error })))
                    // )
                    // }),
                    // catchError(({ error }) => of(PlasticsActions.LoadBrowsePlasticsProductsQualityAlertData.failure({ error })))
                )
            }),
            catchError(({ error }) => of(PlasticsActions.LoadBrowsePlasticsProductsQualityAlertData.failure({ error })))
        ))
    ));

    LoadViewPlasticsProductsQualityAlertData$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadViewPlasticsProductsQualityAlertData.initial),
        mergeMap(({ qualityAlertId }) =>
            this.plasticsApiService.loadPlasticsQualityAlertsById(qualityAlertId).pipe(
                switchMap((qualityAlert) =>
                    forkJoin({
                        mappingComplaintLogs: this.plasticsApiService.loadMappingComplaintLogToQualityAlertsByQualityAlertId(qualityAlertId),
                        mappingProductVersions: this.plasticsApiService.loadMappingProductVersionToQualityAlertsByQualityAlertId(qualityAlertId)
                    }).pipe(
                        switchMap(({ mappingComplaintLogs, mappingProductVersions }) => {
                            const complaintLogIds = mappingComplaintLogs.map(v => v.complaintLogId);
                            const productVersionIds = mappingProductVersions.map(v => v.productVersionId);

                            return forkJoin({
                                complaintLogs: this.plasticsApiService.loadComplaintLogsByIds(complaintLogIds),
                                productVersions: this.plasticsApiService.loadProductVersionsByIds(productVersionIds)
                            }).pipe(
                                map(({ complaintLogs, productVersions }) => {
                                    const releasedProductVersions = productVersions.filter(
                                        v => v.status === ProductVersionStatus.Released
                                    );
                                    return PlasticsActions.LoadViewPlasticsProductsQualityAlertData.success({
                                        qualityAlert,
                                        complaintLogs,
                                        releasedProductVersions
                                    });
                                })
                            );
                        })
                    )
                ),
                catchError(({ error }) =>
                    of(PlasticsActions.LoadViewPlasticsProductsQualityAlertData.failure({ error }))
                )
            )
        )
    ));


    SetPlasticsQualityAlert$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetPlasticsQualityAlert.initial),
        mergeMap(({ qualityAlert }) => this.plasticsApiService.setPlasticsQualityAlert(qualityAlert).pipe(
            map(() => PlasticsActions.SetPlasticsQualityAlert.success({ qualityAlert })),
            catchError(({ error }) => of(PlasticsActions.SetPlasticsQualityAlert.failure({ error })))
        ))
    ));



    SavePlasticsFinishingTask$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SavePlasticsFinishingTask.initial),
        withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
        mergeMap(([{ task }, currentUser]) => {
            const updatedTask: PlasticsFinishingTask = {
                ...task,
                updatedById: currentUser.id,
                updatedOn: new Date(),
            };
            return this.plasticsApiService.setPlasticsFinishingTask(updatedTask).pipe(
                map(() => PlasticsActions.SavePlasticsFinishingTask.success({ task: updatedTask })),
                catchError(({ error }) => of(PlasticsActions.SavePlasticsFinishingTask.failure({ error })))
            )
        })
    ));

    LoadActivePlasticsFinishingTasks$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadActivePlasticsFinishingTasks.initial),
        mergeMap(() => this.plasticsApiService.loadActivePlasticsFinishingTasks().pipe(
            map((tasks) => PlasticsActions.LoadActivePlasticsFinishingTasks.success({ tasks })),
            catchError(({ error }) => of(PlasticsActions.LoadActivePlasticsFinishingTasks.failure({ error })))
        ))
    ));

    LoadPlasticsFinishingStations$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadPlasticsFinishingStations.initial),
        mergeMap(() => this.plasticsApiService.loadPlasticsFinishingStations().pipe(
            map((stations) => PlasticsActions.LoadPlasticsFinishingStations.success({ stations })),
            catchError(({ error }) => of(PlasticsActions.LoadPlasticsFinishingStations.failure({ error })))
        ))

    ));

    SetFinishingStation$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetFinishingStation.initial),
        mergeMap(({ station }) => this.plasticsApiService.setPlasticsFinishingStation(station).pipe(
            map((stations) => PlasticsActions.SetFinishingStation.success({ station })),
            catchError(({ error }) => of(PlasticsActions.SetFinishingStation.failure({ error })))
        ))
    ));

    LoadOrderAndOrderedProductsById$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadOrderAndOrderedProductsById.initial),
        mergeMap(({ orderId }) => {
            return forkJoin([
                this.plasticsApiService.loadOrderById(orderId),
                this.plasticsApiService.loadOrderedProductsByOrderId(orderId)
            ]).pipe(
                map(([order, orderedProducts]) => {
                    return PlasticsActions.LoadOrderAndOrderedProductsById.success({ order, orderedProducts })
                }),
                catchError((error) => of(PlasticsActions.LoadOrderAndOrderedProductsById.failure({ error }))),
            )
        })
    ));

    LoadOrderedProductsByCustomerId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadOrderedProductsByCustomerId.initial),
        mergeMap(({ customerId }) => {
            return this.plasticsApiService.loadOrderedProductsByCustomerId(customerId).pipe(
                map((orderedProducts) => PlasticsActions.LoadOrderedProductsByCustomerId.success({ orderedProducts })),
                catchError((error) => of(PlasticsActions.LoadOrderedProductsByCustomerId.failure({ error }))),
            )
        })
    ));

    LoadOrderAndOrderedProductByOrderedProductId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadOrderAndOrderedProductByOrderedProductId.initial),
        mergeMap(({ orderedProductId }) => this.plasticsApiService.loadOrderedProductById(orderedProductId).pipe(
            switchMap((orderedProduct) => this.plasticsApiService.loadOrderById(orderedProduct.plasticsOrderId).pipe(
                map((order) => PlasticsActions.LoadOrderAndOrderedProductByOrderedProductId.success({ order, orderedProduct })),
                catchError((error) => of(PlasticsActions.LoadOrderAndOrderedProductByOrderedProductId.failure({ error })))
            )),
            catchError((error) => of(PlasticsActions.LoadOrderAndOrderedProductByOrderedProductId.failure({ error })))
        ))
    ))

    // LoadViewOrderData$ = createEffect(() => this.actions$.pipe(
    //     ofType(PlasticsActions.LoadViewOrderData.initial),
    //     mergeMap(({ orderId }) => {
    //         return forkJoin([
    //             this.plasticsApiService.loadOrderById(orderId),
    //             this.plasticsApiService.loadOrderedProductsByOrderId(orderId)
    //         ]).pipe(
    //             mergeMap(([order, orderedProducts]) => {
    //                 const productIds = orderedProducts.map(op => op.productId);
    //                 const orderedProductIds = orderedProducts.map(op => op.id)

    //                 return forkJoin([
    //                     this.plasticsApiService.loadProductsAndReleasedProductVersionsByIds(productIds),
    //                     this.plasticsApiService.loadTasksByOrderedProductIds(orderedProductIds)
    //                 ]).pipe(
    //                     mergeMap(([{ products, productVersions }, { mixingTasks, moldingTasks, finishingTasks }]) => {

    //                         const materialIds = [...new Set(mixingTasks.map(t => t.materialId))];
    //                         const customerIds = [...new Set([
    //                             ...orderedProducts.map(t => t.customerId),
    //                             ...productVersions.map(v => v.customerId)
    //                         ])];
    //                         const mixingTaskAssociatedProductIds = [...new Set(mixingTasks.map(t => t.associatedProductId).filter(productId => !!productId))];
    //                         const materials$ = this.plasticsApiService.loadMaterialsByIds(materialIds);
    //                         const customers$ = this.prismCommonApiService.loadCustomersByIds(customerIds);

    //                         return forkJoin([
    //                             customers$,
    //                             materials$,
    //                             this.plasticsApiService.loadProductsAndReleasedProductVersionsByIds(mixingTaskAssociatedProductIds)
    //                         ]).pipe(
    //                             map(([customers,materials,{
    //                                 productVersions: mixingTaskAssociatedProductVersions, 
    //                                 products: mixingTaskAssociatedProducts
    //                             }]) => {
    //                                 return PlasticsActions.LoadViewOrderData.success({
    //                                     order,
    //                                     orderedProducts,
    //                                     products: MergeArrayValues(products,mixingTaskAssociatedProducts),
    //                                     releasedProductVersions: MergeArrayValues(productVersions,mixingTaskAssociatedProductVersions),
    //                                     mixingTasks,
    //                                     moldingTasks,
    //                                     finishingTasks,
    //                                     materials,
    //                                     customers,
    //                                 })
    //                             })
    //                         );


    //                     }),
    //                     catchError((error) => of(PlasticsActions.LoadViewOrderData.failure({ error }))),
    //                 )
    //             }),
    //             catchError((error) => of(PlasticsActions.LoadViewOrderData.failure({ error }))),
    //         )
    //     }),
    //     catchError((error) => of(PlasticsActions.LoadViewOrderData.failure({ error }))),
    // ));

    LoadOrdersAndOrderedProductsByProductId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadOrdersAndOrderedProductsByProductId.initial),
        mergeMap(({ productId }) => this.plasticsApiService.loadOrderedProductsByProductId(productId).pipe(
            switchMap((orderedProducts) => {
                const orderIds = [...new Set(orderedProducts.map((op) => op.plasticsOrderId))]

                return this.plasticsApiService.loadOrdersByIds(orderIds).pipe(
                    map((orders) => PlasticsActions.LoadOrdersAndOrderedProductsByProductId.success({ orders, orderedProducts }))
                )
            }),
            catchError((error) => of(PlasticsActions.LoadOrdersAndOrderedProductsByProductId.failure({ error })))
        ))
    ))

    SetOrder$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetOrder.initial),
        mergeMap(({ order }) => {
            return this.plasticsApiService.setOrder(order).pipe(
                map(() => PlasticsActions.SetOrder.success({ order })),
                catchError((error) => of(PlasticsActions.SetOrder.failure({ error }))),
            )
        })
    ));

    DeleteOrder$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeleteOrder.initial),
        mergeMap(({ orderId }) => {
            return this.plasticsApiService.deleteOrder(orderId).pipe(
                map(() => PlasticsActions.DeleteOrder.success({ orderId })),
                catchError((error) => of(PlasticsActions.DeleteOrder.failure({ error }))),
            )
        })
    ));

    SetOrderedProducts$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetOrderedProducts.initial),
        mergeMap(({ orderedProducts }) => {
            return this.plasticsApiService.setOrderedProducts(orderedProducts).pipe(
                map(() => PlasticsActions.SetOrderedProducts.success({ orderedProducts })),
                catchError((error) => of(PlasticsActions.SetOrderedProducts.failure({ error }))),
            )
        })
    ));

    DeleteOrderedProduct$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeleteOrderedProduct.initial),
        mergeMap(({ orderedProductId }) => {
            return this.plasticsApiService.deleteOrderedProduct(orderedProductId).pipe(
                map(() => PlasticsActions.DeleteOrderedProduct.success({ orderedProductId })),
                catchError((error) => of(PlasticsActions.DeleteOrderedProduct.failure({ error }))),
            )
        })
    ));

    LoadBrowseOrdersData$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadBrowseOrdersData.initial),
        switchMap(() => {
            const defaultOrderedProductsStatusArr: PlasticsOrderedProductStatus[] = [
                PlasticsOrderedProductStatus.Draft,
                PlasticsOrderedProductStatus.Released,
                PlasticsOrderedProductStatus.ToBeCanceled,
                PlasticsOrderedProductStatus.Hold,
                PlasticsOrderedProductStatus.Blanket,
                PlasticsOrderedProductStatus.Scheduled,
            ]

            const dateLimitedOrderedProductsStatusArr: PlasticsOrderedProductStatus[] = [
                PlasticsOrderedProductStatus.Complete,
                PlasticsOrderedProductStatus.Canceled,
            ]

            const twoDaysAgo = new Date();
            twoDaysAgo.setDate(twoDaysAgo.getDate() - 2)

            const orderedProducts$ = forkJoin([
                this.plasticsApiService.loadOrderedProductsByStatuses(defaultOrderedProductsStatusArr),
                this.plasticsApiService.loadOrderedProductsByStatuses(dateLimitedOrderedProductsStatusArr, twoDaysAgo),
            ]).pipe(
                map(([defaultOrderedProducts, dateLimitedOrderedProducts]) => [...defaultOrderedProducts, ...dateLimitedOrderedProducts])
            )

            const skids$ = forkJoin([
                this.plasticsApiService.loadSkidsByOrderedProductCompletedOn(null),
                this.plasticsApiService.loadSkidsByOrderedProductCompletedOn(twoDaysAgo),
            ]).pipe(
                map(([incompleteSkids, completeSkids]) => [...incompleteSkids, ...completeSkids]),
            )

            const versions$ = this.plasticsApiService.loadReleasedProductVersions()
            return forkJoin([
                orderedProducts$,
                versions$,
                skids$,
            ]).pipe(
                switchMap(([
                    orderedProducts,
                    productVersions,
                    skids,
                ]) => {
                    const plasticsOrderIds = Array.from(new Set(orderedProducts.map(op => op.plasticsOrderId)));;

                    return this.plasticsApiService.loadOrdersByIds([...plasticsOrderIds]).pipe(
                        map(orders => {
                            return PlasticsActions.LoadBrowseOrdersData.success({
                                orders,
                                orderedProducts,
                                productVersions,
                                skids
                            })
                        })
                    )
                })
            )
        }),
        catchError((error) => of(PlasticsActions.LoadBrowseOrdersData.failure({ error })))
    ));

    SetCalendarEvent$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetCalendarEvent.initial),
        mergeMap(({ calendarEvent }) => this.plasticsApiService.setCalendarEvent(calendarEvent).pipe(
            map(() => PlasticsActions.SetCalendarEvent.success({ calendarEvent })),
            catchError((error) => of(PlasticsActions.SetCalendarEvent.failure({ error })))
        ))
    ))

    SetCalendarEvents$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetCalendarEvents.initial),
        mergeMap(({ calendarEvents }) => this.plasticsApiService.setCalendarEvents(calendarEvents).pipe(
            map(() => PlasticsActions.SetCalendarEvents.success({ calendarEvents })),
            catchError((error) => of(PlasticsActions.SetCalendarEvents.failure({ error })))
        ))
    ))

    LoadCalendarEvents$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadCalendarEvents.initial),
        mergeMap(() => this.plasticsApiService.loadCalendarEvents().pipe(
            map((calendarEvents) => PlasticsActions.LoadCalendarEvents.success({ calendarEvents })),
            catchError((error) => of(PlasticsActions.LoadCalendarEvents.failure({ error })))
        ))
    ))
    DeleteCalendarEventsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeleteCalendarEventsByIds.initial),
        mergeMap(({ calendarEventIds }) => this.plasticsApiService.deleteCalendarEventsByIds(calendarEventIds).pipe(
            map(() => PlasticsActions.DeleteCalendarEventsByIds.success({ calendarEventIds })),
            catchError((error) => of(PlasticsActions.DeleteCalendarEventsByIds.failure({ error })))
        ))
    ))

    LoadCalendarEventsBetween$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadCalendarEventsBetween.initial),
        mergeMap(({ startDate, endDate }) => this.plasticsApiService.loadCalendarEventsBetween(startDate, endDate).pipe(
            map((calendarEvents) => PlasticsActions.LoadCalendarEventsBetween.success({ calendarEvents })),
            catchError((error) => of(PlasticsActions.LoadCalendarEventsBetween.failure({ error })))
        ))
    ))

    DeleteCalendarEvent$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeleteCalendarEvent.initial),
        mergeMap(({ calendarEvent }) => this.plasticsApiService.deleteCalendarEvent(calendarEvent).pipe(
            map(() => PlasticsActions.DeleteCalendarEvent.success({ calendarEvent })),
            catchError((error) => of(PlasticsActions.DeleteCalendarEvent.failure({ error })))
        ))
    ))

    LoadComplaintLogs$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadComplaintLogs.initial),
        mergeMap(() => this.plasticsApiService.loadComplaintLogs().pipe(
            map((complaintLogs) => PlasticsActions.LoadComplaintLogs.success({ complaintLogs })),
            catchError((error) => of(PlasticsActions.LoadComplaintLogs.failure({ error })))
        ))
    ))

    LoadComplaintLogById$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadComplaintLogById.initial),
        mergeMap(({ logId }) => this.plasticsApiService.loadComplaintLogById(logId).pipe(
            map((complaintLog) => PlasticsActions.LoadComplaintLogById.success({ complaintLog })),
            catchError((error) => of(PlasticsActions.LoadComplaintLogById.failure({ error })))
        ))
    ))

    SetComplaintLog$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetComplaintLog.initial),
        mergeMap(({ complaintLog }) => this.plasticsApiService.setComplaintLog(complaintLog).pipe(
            map(() => PlasticsActions.SetComplaintLog.success({ complaintLog })),
            catchError((error) => of(PlasticsActions.SetComplaintLog.failure({ error })))
        ))
    ))

    DeleteComplaintLog$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeleteComplaintLog.initial),
        mergeMap(({ complaintLog }) => this.plasticsApiService.deleteComplaintLog(complaintLog).pipe(
            map(() => PlasticsActions.DeleteComplaintLog.success({ complaintLog })),
            catchError((error) => of(PlasticsActions.DeleteComplaintLog.failure({ error })))
        ))
    ))

    LoadViewComplaintLogData$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadViewComplaintLogData.initial),
        mergeMap(({ logId }) => this.plasticsApiService.loadComplaintLogById(logId).pipe(
            mergeMap((complaintLog) => forkJoin(
                [
                    this.plasticsApiService.loadComplaintLogProductsByComplaintLogId(logId),
                    this.plasticsApiService.loadMappingComplaintLogToQualityAlertsByComplaintLogId(logId),
                    this.plasticsApiService.loadMappingComplaintLogToContactsByComplaintLogId(logId)
                ]
            ).pipe(
                map(([complaintLogProducts, mappingComplaintLogToQualityAlert, mappingComplaintLogToContact]) => ({
                    complaintLogProducts,
                    productIds: complaintLogProducts.map(p => p.productId),
                    mappingComplaintLogToQualityAlert,
                    mappingComplaintLogToContact
                })),
                mergeMap(({ complaintLogProducts, productIds, mappingComplaintLogToQualityAlert, mappingComplaintLogToContact }) => this.plasticsApiService.loadReleasedProductVersionsByProductIds(productIds).pipe(
                    map((productVersions) => ({
                        complaintLogProducts,
                        productVersions,
                    })),
                    mergeMap(({ complaintLogProducts, productVersions }) => this.prismCommonApiService.loadCustomerById(complaintLog.customerId).pipe(
                        map((customer) => PlasticsActions.LoadViewComplaintLogData.success({
                            complaintLog,
                            complaintLogProducts,
                            productVersions,
                            customer,
                            mappingComplaintLogToQualityAlert,
                            mappingComplaintLogToContact,
                        }))
                    )),
                    catchError((error) => of(PlasticsActions.LoadViewComplaintLogData.failure({ error })))
                )),
                catchError((error) => of(PlasticsActions.LoadViewComplaintLogData.failure({ error })))
            )),
            catchError((error) => of(PlasticsActions.LoadViewComplaintLogData.failure({ error })))
        )),
        catchError((error) => of(PlasticsActions.LoadViewComplaintLogData.failure({ error })))
    ))

    SetComplaintProduct$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetComplaintProduct.initial),
        mergeMap(({ complaintLogProduct }) => this.plasticsApiService.setComplaintLogProduct(complaintLogProduct).pipe(
            map(() => PlasticsActions.SetComplaintProduct.success({ complaintLogProduct })),
            catchError((error) => of(PlasticsActions.SetComplaintProduct.failure({ error })))
        ))
    ))

    DeleteComplaintProduct$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeleteComplaintProduct.initial),
        mergeMap(({ complaintLogProduct }) => this.plasticsApiService.deleteComplaintLogProduct(complaintLogProduct).pipe(
            map(() => PlasticsActions.DeleteComplaintProduct.success({ complaintLogProduct })),
            catchError((error) => of(PlasticsActions.DeleteComplaintProduct.failure({ error })))
        ))
    ))

    LoadComplaintLogsByProductId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadComplaintLogsByProductId.initial),
        mergeMap(({ productId }) => this.plasticsApiService.loadComplaintLogsByProductId(productId).pipe(
            map((complaintLogs) => PlasticsActions.LoadComplaintLogsByProductId.success({ complaintLogs })),
            catchError((error) => of(PlasticsActions.LoadComplaintLogsByProductId.failure({ error })))
        ))
    ))

    loadComplaintLogsByCustomerId = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadComplaintLogsByCustomerId.initial),
        mergeMap(({ customerId }) => this.plasticsApiService.loadComplaintLogsByCustomerId(customerId).pipe(
            map((complaintLogs) => PlasticsActions.LoadComplaintLogsByCustomerId.success({ complaintLogs })),
            catchError((error) => of(PlasticsActions.LoadComplaintLogsByCustomerId.failure({ error })))
        ))
    ))

    LoadBrowseComplaintLogData$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadBrowseComplaintLogData.initial),
        mergeMap(() => forkJoin([
            this.plasticsApiService.loadComplaintLogs(),
            this.plasticsApiService.loadComplaintLogProducts(),
            this.plasticsApiService.loadReleasedProductVersions(),
            this.prismCommonApiService.loadPlasticsCustomers(),
        ]).pipe(
            map(([complaintLogs,
                complaintLogProducts,
                productVersions,
                customers]) => PlasticsActions.LoadBrowseComplaintLogData.success({
                    complaintLogs,
                    complaintLogProducts,
                    productVersions,
                    customers
                })
            )
        ))
    ))

    // LoadManageMaterialMixingTasksData = createEffect(() => this.actions$.pipe(
    //     ofType(PlasticsActions.LoadManageMaterialMixingTasksData.initial),
    //     mergeMap(() => {
    //         const mixingTasks$ = this.plasticsApiService.loadMaterialMixingTasks();
    //         const materials$ = this.plasticsApiService.loadMaterials();
    //         const orderedProducts$ = this.plasticsApiService.loadOrderedProducts();
    //         return forkJoin([
    //             mixingTasks$,
    //             materials$,
    //             orderedProducts$
    //         ]).pipe(
    //             mergeMap(([materialMixingTasks, materials, orderedProducts]) => {
    //                 const customerIds = [...new Set(orderedProducts.map(p => p.customerId))];
    //                 const productIds = [...new Set([
    //                     ...orderedProducts.map(p => p.productId),
    //                     ...materialMixingTasks.map(t => t.associatedProductId).filter(productId => !!productId)
    //                 ])];
    //                 const customers$ = this.prismCommonApiService.loadCustomersByIds(customerIds);
    //                 const productVersions$ = this.plasticsApiService.loadReleasedProductVersionsByProductIds(productIds)

    //                 return forkJoin([
    //                     customers$,
    //                     productVersions$
    //                 ]).pipe(
    //                     map(([customers, productVersions]) => {
    //                         return PlasticsActions.LoadManageMaterialMixingTasksData.success({
    //                             materialMixingTasks,
    //                             materials,
    //                             orderedProducts,
    //                             customers,
    //                             productVersions,
    //                         })
    //                     }),
    //                     catchError((error) => of(PlasticsActions.LoadManageMaterialMixingTasksData.failure({ error })))
    //                 )
    //             }),
    //             catchError((error) => of(PlasticsActions.LoadManageMaterialMixingTasksData.failure({ error })))
    //         )
    //     }),
    //     catchError((error) => of(PlasticsActions.LoadManageMaterialMixingTasksData.failure({ error })))
    // ))

    SetLabelInstance$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetLabelInstance.initial),
        mergeMap(({ labelInstance }) => this.plasticsApiService.setLabelInstance(labelInstance).pipe(
            map(() => PlasticsActions.SetLabelInstance.success({ labelInstance })),
            catchError((error) => of(PlasticsActions.SetLabelInstance.failure({ error })))
        ))
    ))

    LoadLabelInstanceById$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadLabelInstanceById.initial),
        mergeMap(({ labelInstanceId }) => this.plasticsApiService.loadLabelInstanceById(labelInstanceId).pipe(
            map((labelInstance) => PlasticsActions.LoadLabelInstanceById.success({ labelInstance })),
            catchError((error) => of(PlasticsActions.LoadLabelInstanceById.failure({ error })))
        ))
    ))

    LoadLabelInstancesByIds$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadLabelInstancesByIds.initial),
        mergeMap(({ labelInstanceIds }) => this.plasticsApiService.loadLabelInstancesByIds(labelInstanceIds).pipe(
            map((labelInstances) => PlasticsActions.LoadLabelInstancesByIds.success({ labelInstances })),
            catchError((error) => of(PlasticsActions.LoadLabelInstancesByIds.failure({ error })))
        ))
    ))

    SetMappingsComplaintLogToContact$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetMappingComplaintLogToContact.initial),
        mergeMap(({ mappings }) => this.plasticsApiService.setMappingsComplaintLogToContact(mappings).pipe(
            map(() => PlasticsActions.SetMappingComplaintLogToContact.success({ mappings })),
            catchError((error) => of(PlasticsActions.SetMappingComplaintLogToContact.failure({ error })))

        ))
    ))

    DeleteMappingComplaintLogToContact$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeleteMappingComplaintLogToContact.initial),
        mergeMap(({ mapping }) => this.plasticsApiService.deleteMappingComplaintLogToContact(mapping).pipe(
            map(() => PlasticsActions.DeleteMappingComplaintLogToContact.success({ mapping })),
            catchError((error) => of(PlasticsActions.DeleteMappingComplaintLogToContact.failure({ error })))
        ))
    ))

    LoadComplaintLogsByContactId$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadComplaintLogsByContactId.initial),
        mergeMap(({ contactId }) => this.plasticsApiService.loadMappingComplaintLogToContactsByContactId(contactId).pipe(
            mergeMap((mappings) => this.plasticsApiService.loadComplaintLogsByIds(mappings.map(m => m.complaintLogId)).pipe(
                map((complaintLogs) => PlasticsActions.LoadComplaintLogsByContactId.success({ complaintLogs })),
                catchError((error) => of(PlasticsActions.LoadComplaintLogsByContactId.failure({ error })))
            )),
            catchError((error) => of(PlasticsActions.LoadComplaintLogsByContactId.failure({ error })))
        ))
    ))

    SetSkid$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetSkid.initial),
        mergeMap(({ skid }) => this.plasticsApiService.setSkid(skid).pipe(
            map(() => PlasticsActions.SetSkid.success({ skid })),
            catchError((error) => of(PlasticsActions.SetSkid.failure({ error })))
        ))
    ))

    SetSkids$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.SetSkids.initial),
        mergeMap(({ skids }) => this.plasticsApiService.setSkids(skids).pipe(
            map(() => PlasticsActions.SetSkids.success({ skids })),
            catchError((error) => of(PlasticsActions.SetSkids.failure({ error })))
        ))
    ))

    LoadSkids$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadSkids.initial),
        mergeMap(() => this.plasticsApiService.loadSkids().pipe(
            map((skids) => PlasticsActions.LoadSkids.success({ skids })),
            catchError((error) => of(PlasticsActions.LoadSkids.failure({ error })))
        ))
    ))

    LoadSkidById$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.LoadSkidById.initial),
        mergeMap(({ skidId }) => this.plasticsApiService.loadSkidById(skidId).pipe(
            map((skid) => PlasticsActions.LoadSkidById.success({ skid })),
            catchError((error) => of(PlasticsActions.LoadSkidById.failure({ error })))
        ))
    ))

    DeleteSkid$ = createEffect(() => this.actions$.pipe(
        ofType(PlasticsActions.DeleteSkid.initial),
        mergeMap(({ skidId }) => this.plasticsApiService.deleteDockInspectionInstancesAndSkidBySkidId(skidId).pipe(
            map(() => PlasticsActions.DeleteSkid.success({ skidId })),
            catchError((error) => of(PlasticsActions.DeleteSkid.failure({ error })))
        ))
    ))
}
